import { Button } from '@hover/blueprint';
import React from 'react';
import { generatePath } from 'react-router';

import { useIsMobileSize, useIsSharedView } from 'src/modules/renderings/hooks';
import { useRenderingSelection } from 'src/modules/renderings/hooks/useRenderingSelection/useRenderingSelection';
import { Link } from 'src/shared/components';
import type { HiDefRenderingFile } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useReactNativeWebView,
  useTranslate,
} from 'src/shared/hooks';
import { Route } from 'src/shared/route';

export const isRenderingFileCompositorReady = (
  renderingFile: HiDefRenderingFile,
) =>
  !!renderingFile.uvPass &&
  !!renderingFile.cryptomattePass &&
  !!renderingFile.combinedLightingPass;

/**
 * A link to interactive renderings styled as a button sized appropriately for placement above the carousel image. This
 * component also manages hiding or showing itself based on screen size.
 */
export const InteractiveRenderingsLink = () => {
  const { rendering, renderingFile } = useRenderingSelection();
  const isSharedView = useIsSharedView();
  const isMobileSize = useIsMobileSize();
  const [isWebView] = useReactNativeWebView();

  const t = useTranslate(TranslationNamespace.SHARED);

  if (isSharedView || isMobileSize) {
    return null;
  }

  if (!rendering || !renderingFile) {
    return null;
  }

  if (!isRenderingFileCompositorReady(renderingFile)) {
    return null;
  }

  const path = generatePath(Route.RENDERINGS_INTERACTIVE.path, {
    renderingId: rendering.id,
    renderingFileId: renderingFile.id,
  });

  // isExternal is used to open the link in a new tab, the link does not actually point to an external resource
  return (
    <Link
      _hover={{ textDecoration: 'none' }}
      isExternal={!isWebView}
      textDecoration="none"
      to={path}
    >
      <Button fontSize="200" fontWeight="400">
        {t('renderings.interactive.linkFromHub')}
      </Button>
    </Link>
  );
};
